import { GameState, Position, PieceType, getAllLegalMoves } from './chess-engine';
import { getBestMoveFromStockfish, clampStockfishElo, type StockfishOptions } from './stockfish-engine';

export interface Move {
  from: Position;
  to: Position;
  promotion?: PieceType;
}

/**
 * Converts UCI notation (e.g., "e2e4") to Move object
 */
export function parseUciMove(uci: string): Move | null {
  if (!uci || uci.length < 4) return null;
  
  const fromFile = uci[0];
  const fromRank = uci[1];
  const toFile = uci[2];
  const toRank = uci[3];
  
  // Convert algebraic notation to array indices
  const fromCol = fromFile.charCodeAt(0) - 97; // 'a' = 0
  const fromRow = 8 - parseInt(fromRank); // '8' = 0, '1' = 7
  const toCol = toFile.charCodeAt(0) - 97;
  const toRow = 8 - parseInt(toRank);
  
  // Check bounds
  if (fromCol < 0 || fromCol > 7 || fromRow < 0 || fromRow > 7 ||
      toCol < 0 || toCol > 7 || toRow < 0 || toRow > 7) {
    return null;
  }
  
  const move: Move = {
    from: [fromRow, fromCol],
    to: [toRow, toCol]
  };
  
  // Handle promotion
  if (uci.length === 5) {
    const promotionChar = uci[4].toLowerCase();
    switch (promotionChar) {
      case 'q': move.promotion = 'Q'; break;
      case 'r': move.promotion = 'R'; break;
      case 'b': move.promotion = 'B'; break;
      case 'n': move.promotion = 'N'; break;
    }
  }
  
  return move;
}

/**
 * Converts Move object to UCI notation (e.g., "e2e4")
 */
export function formatMoveToUci(move: Move): string {
  const fromFile = String.fromCharCode(97 + move.from[1]); // 0 = 'a'
  const fromRank = String(8 - move.from[0]); // 0 = '8'
  const toFile = String.fromCharCode(97 + move.to[1]);
  const toRank = String(8 - move.to[0]);
  
  let uci = fromFile + fromRank + toFile + toRank;
  
  if (move.promotion) {
    switch (move.promotion) {
      case 'Q': uci += 'q'; break;
      case 'R': uci += 'r'; break;
      case 'B': uci += 'b'; break;
      case 'N': uci += 'n'; break;
    }
  }
  
  return uci;
}

/**
 * Maps bot ELO to appropriate Stockfish skill level (0-20)
 * Uses skill levels instead of ELO for more realistic strength
 */
export function mapEloToStockfish(botElo: number): number {
  // Clamp input to bot range
  const clampedBotElo = Math.max(200, Math.min(3200, botElo));
  
  // Map to skill levels for more realistic play
  let skillLevel: number;
  if (clampedBotElo <= 400) skillLevel = 0;      // ~800-1000 ELO
  else if (clampedBotElo <= 700) skillLevel = 2;  // ~1100-1200 ELO  
  else if (clampedBotElo <= 900) skillLevel = 4;  // ~1300-1400 ELO
  else if (clampedBotElo <= 1200) skillLevel = 6; // ~1500-1600 ELO
  else if (clampedBotElo <= 1400) skillLevel = 8; // ~1700-1800 ELO
  else if (clampedBotElo <= 2000) skillLevel = 12; // ~2000-2100 ELO
  else if (clampedBotElo <= 2800) skillLevel = 16; // ~2400-2500 ELO
  else skillLevel = 20; // ~2800+ ELO
  
  // Convert skill level to approximate ELO for Stockfish (skill 0 ≈ 800, skill 20 ≈ 3000)
  const approximateElo = 800 + (skillLevel * 110); // Linear approximation
  
  return clampStockfishElo(approximateElo);
}

/**
 * Gets current position as FEN string from GameState
 */
function gameStateToFen(gameState: GameState): string {
  // Build FEN piece placement
  let fen = '';
  for (let row = 0; row < 8; row++) {
    let emptyCount = 0;
    for (let col = 0; col < 8; col++) {
      const piece = gameState.board[row][col];
      if (piece) {
        if (emptyCount > 0) {
          fen += emptyCount;
          emptyCount = 0;
        }
        // Convert piece to FEN notation
        let pieceChar = piece.type.toLowerCase();
        if (piece.color === 'white') {
          pieceChar = pieceChar.toUpperCase();
        }
        fen += pieceChar;
      } else {
        emptyCount++;
      }
    }
    if (emptyCount > 0) {
      fen += emptyCount;
    }
    if (row < 7) fen += '/';
  }
  
  // Add side to move
  fen += ' ' + (gameState.sideToMove === 'white' ? 'w' : 'b');
  
  // Add castling availability using castling rights from game state
  let castling = '';
  if (gameState.castlingRights.whiteKingSide) castling += 'K';
  if (gameState.castlingRights.whiteQueenSide) castling += 'Q';
  if (gameState.castlingRights.blackKingSide) castling += 'k';
  if (gameState.castlingRights.blackQueenSide) castling += 'q';
  fen += ' ' + (castling || '-');
  
  // Add en passant target
  fen += ' ' + (gameState.enPassantTarget ? 
    String.fromCharCode(97 + gameState.enPassantTarget[1]) + (8 - gameState.enPassantTarget[0]) : 
    '-');
  
  // Add halfmove and fullmove numbers
  fen += ' ' + gameState.halfmoveClock + ' ' + gameState.fullmoveNumber;
  
  return fen;
}

/**
 * Gets the best move from Stockfish for a given game state and ELO
 */
export async function getStockfishMove(gameState: GameState, botElo: number): Promise<Move | null> {
  try {
    const fen = gameStateToFen(gameState);
    const stockfishElo = mapEloToStockfish(botElo);
    
    // Calculate appropriate movetime based on ELO
    const movetime = Math.max(500, Math.min(3000, botElo));
    
    const options: StockfishOptions = {
      elo: stockfishElo,
      movetime,
      depth: Math.max(1, Math.min(6, Math.floor(botElo / 500)))
    };
    
    const uciMove = await getBestMoveFromStockfish(fen, options);
    
    if (!uciMove) return null;
    
    const move = parseUciMove(uciMove);
    
    // Verify the move is legal
    if (move) {
      const legalMoves = getAllLegalMoves(gameState);
      const isLegal = legalMoves.some(legalMove => 
        legalMove.from[0] === move.from[0] && legalMove.from[1] === move.from[1] &&
        legalMove.to[0] === move.to[0] && legalMove.to[1] === move.to[1] &&
        legalMove.promotion === move.promotion
      );
      
      if (!isLegal) {
        console.warn('Stockfish returned illegal move:', uciMove, move);
        return null;
      }
    }
    
    return move;
  } catch (error) {
    console.error('Stockfish move failed:', error);
    return null;
  }
}